import { EnergyDeliveryGranularity } from '@core/api/reporting/reporting.models';

import { SingleSiteVehiclesViewModeStoreModelEnum } from '@domain/single-site/single-site.enums';
import { SitesViewModeStoreModelEnum } from '@domain/sites/sites.enums';

import { MovableCardsContainerViewModel } from '@shared/components/movable-cards-container';

import { DashboardCard } from '../../pages/dashboard/enums/dashboard-card.enum';
import { FlexibilityAnalyticsCard } from '../../pages/flexibility/pages/flexibility-analytics/enums/flexibility-analytics-card.enum';
import { EnergyCostRange } from '../../pages/multi-site-dashboard/enums/energy-cost-range.enum';
import { MultiSiteDashboardCard } from '../../pages/multi-site-dashboard/enums/multi-site-dashboard-card.enum';
import { VehicleSocSummaryRange } from '../../pages/multi-site-dashboard/enums/vehicle-soc-summary-range.enum';
import { RoamingAnalyticsCard } from '../../pages/roaming/pages/roaming-analytics/enums/roaming-analytics-card.enum';
import { VehicleDetailsOverviewCard } from '../../pages/shared-pages/vehicle-details-overview/enums/vehicle-details-overview-card.enum';
import { SharingAnalyticsCard } from '../../pages/sharing/pages/sharing-analytics/enums/sharing-analytics-card.enum';
import { SingleSiteAnalyticsCard } from '../../pages/single-site/pages/single-site-analytics/enums/single-site-analytics-card.enum';
import { SingleSiteGraphViewState } from '../../pages/single-site/pages/single-site-graph/store/single-site-graph.reducer';
import { SingleSiteOverviewCard } from '../../pages/single-site/pages/single-site-overview/enums/single-site-overiew-card.enum';
import { SingleSiteVehiclesCompactViewCard } from '../../pages/single-site/pages/single-site-vehicles/enums/single-site-vehicles-compact-view-card.enum';
import type { VehicleListScope } from '../stores/vehicle-list/vehicle-list-store.config';

export type SingleSiteOverviewViewMode = 'map' | 'grid' | 'city';
export type GridLabelType = 'vehicle' | 'id' | 'rank' | 'kiosk';

export interface SingleSiteOverviewPreferences {
  viewMode?: SingleSiteOverviewViewMode;
  displayedValueType?: 'soc' | 'power';
  gridLabelType?: GridLabelType;
  selectedZoneId?: string;
}

export interface StorageModel {
  version?: number;
  singleSiteOverviewPreferences?: Record<string, SingleSiteOverviewPreferences>;
  singleSiteOverviewTimelineCollapsed?: boolean;
  singleSiteOverviewCardsLayout?: MovableCardsContainerViewModel<SingleSiteOverviewCard>;
  singleSiteOverviewMobileCardsLayout?: MovableCardsContainerViewModel<SingleSiteOverviewCard>;
  singleSiteVehiclesViewMode?: SingleSiteVehiclesViewModeStoreModelEnum;
  singleSiteVehiclesCompactViewCardLayout?: MovableCardsContainerViewModel<SingleSiteVehiclesCompactViewCard>;
  globalVehiclesViewMode?: SingleSiteVehiclesViewModeStoreModelEnum;
  globalVehiclesCompactViewCardLayout?: MovableCardsContainerViewModel<SingleSiteVehiclesCompactViewCard>;
  sitesViewMode?: SitesViewModeStoreModelEnum;
  sitesMapCollapsed?: boolean;
  mobileSitesSelectedTenants?: string[];
  dashboardCardsLayout?: MovableCardsContainerViewModel<DashboardCard>;
  multiSiteDashboardCardsLayout?: MovableCardsContainerViewModel<MultiSiteDashboardCard>;
  multiSiteDashboardSelectedTenantId?: string;
  globalChargersSelectedTenantId?: string;
  globalVehiclesSelectedTenantId?: string;
  globalFlexibilitySelectedTenantId?: string;
  multiSiteDashboardEnergyCostRange?: EnergyCostRange;
  multiSiteDashboardVehicleSocSummaryRange?: VehicleSocSummaryRange;
  singleFleetSiteAnalyticsCardsLayout?: MovableCardsContainerViewModel<SingleSiteAnalyticsCard>;
  singlePublicSiteAnalyticsCardsLayout?: MovableCardsContainerViewModel<SingleSiteAnalyticsCard>;
  energyDeliveryGranularity?: EnergyDeliveryGranularity;
  roamingAnalyticsCardsLayout?: MovableCardsContainerViewModel<RoamingAnalyticsCard>;
  sharingAnalyticsCardsLayout?: MovableCardsContainerViewModel<SharingAnalyticsCard>;
  flexibilityAnalyticsCardsLayout?: MovableCardsContainerViewModel<FlexibilityAnalyticsCard>;
  vehicleDetailsOverviewCardsLayout?: MovableCardsContainerViewModel<VehicleDetailsOverviewCard>;
  vehicleDetailsOverviewMobileCardsLayout?: MovableCardsContainerViewModel<VehicleDetailsOverviewCard>;
  singleSiteGraphViewState?: SingleSiteGraphViewState;
}

export class StoreToStorageSync {
  private static readonly STORAGE_VERSION = 25;
  private static readonly STORAGE_KEY = 'storeData';

  public static getStorageData(): StorageModel | null {
    const storageData = localStorage.getItem(this.STORAGE_KEY);
    if (!storageData) {
      return null;
    }

    try {
      const parsedStorageData = JSON.parse(storageData);
      if (parsedStorageData.version !== this.STORAGE_VERSION) {
        return null;
      }

      return parsedStorageData;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public static setStorageData(storageData: Partial<StorageModel>): void {
    const currentData = this.getStorageData() || { version: this.STORAGE_VERSION };

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({ ...currentData, ...storageData }));
  }

  public static clearStorageData(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  public static getSingleSiteOverviewPreferences(siteId: string): SingleSiteOverviewPreferences {
    return this.getStorageData()?.singleSiteOverviewPreferences?.[siteId] ?? {};
  }

  public static setSingleSiteOverviewPreferences(siteId: string, partial: Partial<SingleSiteOverviewPreferences>): void {
    const current = this.getStorageData();
    const existing = current?.singleSiteOverviewPreferences?.[siteId] ?? {};
    this.setStorageData({
      singleSiteOverviewPreferences: {
        ...current?.singleSiteOverviewPreferences,
        [siteId]: { ...existing, ...partial },
      },
    });
  }

  public static getMultiSiteDashboardEnergyCostRange(): EnergyCostRange | undefined {
    return this.getStorageData()?.multiSiteDashboardEnergyCostRange;
  }

  public static setMultiSiteDashboardEnergyCostRange(range: EnergyCostRange): void {
    this.setStorageData({ multiSiteDashboardEnergyCostRange: range });
  }

  public static getMultiSiteDashboardSelectedTenantId(): string | undefined {
    return this.getStorageData()?.multiSiteDashboardSelectedTenantId;
  }

  public static setMultiSiteDashboardSelectedTenantId(tenantId: string): void {
    this.setStorageData({ multiSiteDashboardSelectedTenantId: tenantId });
  }

  public static getGlobalChargersSelectedTenantId(): string | undefined {
    return this.getStorageData()?.globalChargersSelectedTenantId;
  }

  public static setGlobalChargersSelectedTenantId(tenantId: string): void {
    this.setStorageData({ globalChargersSelectedTenantId: tenantId });
  }

  public static clearGlobalChargersSelectedTenantId(): void {
    this.setStorageData({ globalChargersSelectedTenantId: undefined });
  }

  public static getGlobalVehiclesSelectedTenantId(): string | undefined {
    return this.getStorageData()?.globalVehiclesSelectedTenantId;
  }

  public static setGlobalVehiclesSelectedTenantId(tenantId: string): void {
    this.setStorageData({ globalVehiclesSelectedTenantId: tenantId });
  }

  public static clearGlobalVehiclesSelectedTenantId(): void {
    this.setStorageData({ globalVehiclesSelectedTenantId: undefined });
  }

  public static getGlobalFlexibilitySelectedTenantId(): string | undefined {
    return this.getStorageData()?.globalFlexibilitySelectedTenantId;
  }

  public static setGlobalFlexibilitySelectedTenantId(tenantId: string): void {
    this.setStorageData({ globalFlexibilitySelectedTenantId: tenantId });
  }

  public static clearGlobalFlexibilitySelectedTenantId(): void {
    this.setStorageData({ globalFlexibilitySelectedTenantId: undefined });
  }

  public static getMultiSiteDashboardVehicleSocSummaryRange(): VehicleSocSummaryRange | undefined {
    return this.getStorageData()?.multiSiteDashboardVehicleSocSummaryRange;
  }

  public static setMultiSiteDashboardVehicleSocSummaryRange(range: VehicleSocSummaryRange): void {
    this.setStorageData({ multiSiteDashboardVehicleSocSummaryRange: range });
  }

  public static getEnergyDeliveryGranularity(): EnergyDeliveryGranularity | undefined {
    return this.getStorageData()?.energyDeliveryGranularity;
  }

  public static setEnergyDeliveryGranularity(granularity: EnergyDeliveryGranularity): void {
    this.setStorageData({ energyDeliveryGranularity: granularity });
  }

  // The vehicles view state (view mode + compact-view card layout) is persisted separately per
  // vehicle-list scope, so the single-site and global vehicles screens each remember their own values.
  public static getVehiclesViewMode(scope: VehicleListScope): SingleSiteVehiclesViewModeStoreModelEnum | undefined {
    const storageData = this.getStorageData();
    return scope === 'global' ? storageData?.globalVehiclesViewMode : storageData?.singleSiteVehiclesViewMode;
  }

  public static setVehiclesViewMode(scope: VehicleListScope, viewMode: SingleSiteVehiclesViewModeStoreModelEnum): void {
    this.setStorageData(scope === 'global' ? { globalVehiclesViewMode: viewMode } : { singleSiteVehiclesViewMode: viewMode });
  }

  public static getVehiclesCompactViewCardLayout(
    scope: VehicleListScope
  ): MovableCardsContainerViewModel<SingleSiteVehiclesCompactViewCard> | undefined {
    const storageData = this.getStorageData();
    return scope === 'global' ? storageData?.globalVehiclesCompactViewCardLayout : storageData?.singleSiteVehiclesCompactViewCardLayout;
  }

  public static setVehiclesCompactViewCardLayout(
    scope: VehicleListScope,
    layout: MovableCardsContainerViewModel<SingleSiteVehiclesCompactViewCard>
  ): void {
    this.setStorageData(
      scope === 'global' ? { globalVehiclesCompactViewCardLayout: layout } : { singleSiteVehiclesCompactViewCardLayout: layout }
    );
  }
}
